/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import type { CosmosCardProps } from "@components/elements/atoms";
import { BlogCardFragment } from "@cosmos/hygraph-schema";
import { cva, cx, RecipeVariantProps } from "@panda/css";
import { BlogCard } from "./BlogCard.molecule";
import { Box } from "@panda/jsx";

const cardStyle = cva({
	base: {
		display: "grid",
		gap: "eds.dimension.16",
	},
	variants: {
		maxRowSize: {
			1: { gridTemplateColumns: { base: "1", mdDown: "1" } },
			2: { gridTemplateColumns: { base: "2", mdDown: "1" } },
			3: { gridTemplateColumns: { base: "3", mdDown: "1" } },
			4: { gridTemplateColumns: { base: "4", mdDown: "1" } },
		},
	},
});

type BlogCardStyleVariants = RecipeVariantProps<typeof cardStyle>;
export type BlogCardsProps = BlogCardStyleVariants & {
	className?: string;
	cards?: BlogCardFragment[] | null;
	direction?: CosmosCardProps["direction"];
	rowSizeOverride?: NonNullable<BlogCardStyleVariants>["maxRowSize"];
	hideCategoryPills?: boolean;
	/** Heading level for each card title (see BlogCard.titleAs). Omit for non-heading UI text. */
	titleAs?: "h2" | "h3" | "h4" | "h5" | "h6";
};

type RowSize = (typeof cardStyle.variantMap.maxRowSize)[number];
const ROW_TOKENS = cardStyle.variantMap.maxRowSize;

const VARIANT_LABELS = ["single", "double", "triple", "quadruple"] as const;
type BlogCardVariant = (typeof VARIANT_LABELS)[number];

const selectByIndex = <T,>(items: readonly T[], n: number): T => items[Math.max(0, Math.min(items.length - 1, n - 1))];
const getRowSize = (count: number, max: number): RowSize => selectByIndex(ROW_TOKENS, Math.min(count, max));
const getVariant = (len: number): BlogCardVariant => selectByIndex(VARIANT_LABELS, len);

//srcset hint sized to actual card width per breakpoint
const SIZES_BY_ROW: Record<RowSize, string> = {
	1: "100vw",
	2: "(max-width: 768px) 100vw, 50vw",
	3: "(max-width: 768px) 100vw, 33vw",
	4: "(max-width: 768px) 100vw, 25vw",
};

export function BlogCards({
	cards = [],
	className,
	maxRowSize = 3,
	direction,
	rowSizeOverride,
	hideCategoryPills,
	titleAs,
}: BlogCardsProps): React.JSX.Element | null {
	if (!cards || !cards.length) return null;

	const rowSize = rowSizeOverride || getRowSize(cards.length, maxRowSize);
	const variant = getVariant(rowSize);

	if (!direction) {
		direction = variant === "single" ? "mdRow" : "column";
	}

	const sizes = SIZES_BY_ROW[rowSize];

	return (
		<Box className={cx("blog-cards", className, cardStyle({ maxRowSize: rowSize }))}>
			{(cards || []).map((card, i) => (
				<BlogCard
					key={`blog-card-grid-item-${i}`}
					{...card}
					variant={variant}
					direction={direction}
					hideCategoryPills={hideCategoryPills}
					sizes={sizes}
					titleAs={titleAs}
				/>
			))}
		</Box>
	);
}
